import { useState } from 'react';
import { MdRemoveCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../Data/ReduxViewModels/Admin';
import {
  Button,
  ButtonGroup,
  Checkbox,
  ConfirmDialog,
  Dropdown,
  Field,
  NumericInput,
  PlaceholderButton,
  TextAreaInput,
  TextInput,
  TextOutput,
} from '../Core';
import { FieldSet, ListFieldSection, PageSection } from '../Layout';

const IntegratorSection = ({ integrator, index }) => {
  const path = useSelector((state) => state.admin.dataIntegratorRun.path);
  const testing = useSelector((state) => state.admin.dataIntegratorRun.testing);
  const event = useSelector((state) => state.admin.dataIntegratorRun.event);
  const success = useSelector((state) => state.admin.dataIntegratorRun.success);
  const error = useSelector((state) => state.admin.dataIntegratorRun.error);
  const connectionNames = useSelector((state) => state.admin.syncModel.DatabaseConnectionNames);
  const isTesting = path === integrator.Name;
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [conditions, setConditions] = useState('');
  const dispatch = useDispatch();
  if (!integrator) return null;
  const runIntegrator = () => {
    dispatch(
      actions.triggerDataIntegratorEvent({
        path: integrator.Name,
        conditions,
        event: 'run',
        timeout: integrator.WebApiTimeout,
      })
    );
  };
  const startStopIntegrator = () => {
    var event = integrator.State ? 'stop' : 'start';
    dispatch(actions.triggerDataIntegratorEvent({ path: integrator.Name, conditions, event }));
  };

  let statusMessage = null;
  let successMessage = null;
  let errorMessage = null;
  if (isTesting) {
    if (success) {
      if (event === 'run') {
        successMessage = 'Run was successful';
      } else if (event === 'start') {
        successMessage = 'Integrator was started';
      } else {
        successMessage = 'Integrator was stopped';
      }
    }
    if (testing) {
      if (event === 'run') {
        statusMessage = 'Running integrator...';
      } else if (event === 'start') {
        statusMessage = 'Starting integrator...';
      } else {
        statusMessage = 'Stopping integrator...';
      }
    }
    errorMessage = error;
  }

  return (
    <FieldSet key={index} disabled={isTesting}>
      <Field label="Name" input={<TextInput />} name={`syncModel.DataIntegrators[${index}].Name`} />
      <Field label="Assembly Name" input={<TextInput />} name={`syncModel.DataIntegrators[${index}].AssemblyName`} />
      <Field label="Plugin Type" input={<TextAreaInput />} name={`syncModel.DataIntegrators[${index}].PluginType`} />
      <Field
        label="Input Database"
        input={<Dropdown />}
        options={connectionNames.map((c) => ({ value: c, label: c }))}
        simpleValue
        name={`syncModel.DataIntegrators[${index}].InputName`}
      />
      <Field
        label="Output Database"
        input={<Dropdown />}
        options={connectionNames.map((c) => ({ value: c, label: c }))}
        simpleValue
        name={`syncModel.DataIntegrators[${index}].OutputName`}
      />

      <Field
        label="Override API Timeout (s)"
        input={<NumericInput />}
        min={60}
        name={`syncModel.DataIntegrators[${index}].WebApiTimeout`}
        additionalInformationDialogContent="Timeout duration (seconds) applied when running the integrator once from this page. The default and minimum is 60 seconds."
      />
      <Field
        label="Show Advanced Settings"
        input={<Checkbox />}
        onChange={(e) => setShowAdvanced(e.data.value)}
        value={showAdvanced}
      />
      {showAdvanced ? (
        <>
          <Field
            label="Recurrence (dd.hh:mm:ss)"
            input={<TextInput />}
            placeholder="dd.hh:mm:ss"
            name={`syncModel.DataIntegrators[${index}].TransferInterval`}
            messages={[{ message: 'How often the data integrator will run', severity: 'info' }]}
          />
          <Field
            label="Start Time (hh:mm:ss)"
            input={<TextInput />}
            placeholder="hh:mm:ss"
            name={`syncModel.DataIntegrators[${index}].TimeDelayUntil`}
            messages={[{ message: 'The time when the first run of the integrator will begin', severity: 'info' }]}
          />
          <Field
            label="Time Delay (hh:mm:ss)"
            input={<TextInput />}
            placeholder="hh:mm:ss"
            name={`syncModel.DataIntegrators[${index}].TimeDelay`}
            messages={[{ message: 'The time to delay before each run of the integrator', severity: 'info' }]}
          />
          <Field
            label="Wait For Integrator Names"
            input={<TextAreaInput />}
            name={`syncModel.DataIntegrators[${index}].WaitForIntegratorNames`}
          />
          <Field label="Config File" input={<TextInput />} name={`syncModel.DataIntegrators[${index}].ConfigFile`} />
          <Field
            label="File System Watch Directory"
            input={<TextInput />}
            name={`syncModel.DataIntegrators[${index}].FileSystemWatchDirectory`}
          />
          <Field
            label="File System Watch Filter"
            input={<TextInput />}
            name={`syncModel.DataIntegrators[${index}].FileSystemWatchFilter`}
          />
          <Field
            label="File System Watch Concurrent Process Limit"
            input={<NumericInput />}
            format={{ nearest: 1 }}
            name={`syncModel.DataIntegrators[${index}].FileSystemWatchConcurrentProcessLimit`}
          />
        </>
      ) : null}
      <Field label="State" input={<TextOutput />} value={integrator.State ? 'Started' : 'Stopped'} />

      <Field
        label="Conditions"
        input={<TextInput />}
        value={conditions}
        onChange={(e) => setConditions(e.data.value)}
        messages={[
          { message: statusMessage, severity: 'info' },
          { message: successMessage, severity: 'success' },
          { message: errorMessage, severity: 'error' },
        ]}
      />
      <ButtonGroup>
        <Button loading={isTesting && testing && event === 'run'} onClick={() => runIntegrator()} primary>
          Run Once
        </Button>
        <Button loading={isTesting && testing && event !== 'run'} onClick={() => startStopIntegrator()} primary>
          {integrator.State ? 'Stop Integrator' : 'Start Integrator'}
        </Button>
      </ButtonGroup>
    </FieldSet>
  );
};

const IntegratorsSection = () => {
  const integrators = useSelector((state) => state.admin.syncModel.DataIntegrators);
  const dispatch = useDispatch();

  const removeIntegrator = (index) => {
    dispatch(
      actions.setValue({
        name: 'syncModel.DataIntegrators',
        value: integrators.filter((_, i) => i !== index),
        sync: true,
      })
    );
  };

  const addIntegrator = () => {
    const exists = (name) => {
      return integrators.some((c) => c.Name === name);
    };
    let index = integrators.length;
    let name = `NewIntegrator_${index}`;
    while (exists(name)) {
      index = index + 1;
      name = `NewIntegrator_${index}`;
    }

    dispatch(
      actions.setValue({ name: 'syncModel.DataIntegrators', value: [...integrators, { Name: name }], sync: true })
    );
  };

  return (
    <ListFieldSection single>
      {integrators
        ? integrators.map((_, i) => [
            <FieldSet key={i}>
              <IntegratorSection integrator={_} index={i} />
              <ButtonGroup align="center">
                <ConfirmDialog
                  confirm
                  confirmText={`Are you sure you want to delete the integrator '${_.Name}'?`}
                  confirmButtonText="Delete Integrator"
                  confirmTitle="Delete Integrator"
                >
                  <Button name={_.Name} subtle error onClick={() => removeIntegrator(i)}>
                    <MdRemoveCircle size="2em" />
                  </Button>
                </ConfirmDialog>
              </ButtonGroup>
            </FieldSet>,
          ])
        : null}
      <div style={{ height: 190, display: 'flex' }}>
        <PlaceholderButton style={{ height: 200 }} onClick={() => addIntegrator()} />
      </div>
    </ListFieldSection>
  );
};

const DataIntegratorsAdmin = () => {
  return (
    <PageSection>
      <IntegratorsSection />
    </PageSection>
  );
};

export default DataIntegratorsAdmin;
export { DataIntegratorsAdmin };
